import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box } from "@mui/material";
import GenericDraggableTableView from "../../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { MAX_COLUMN_SELECTION_COUNT } from "../../../../Helper/DigitalTwin/DataFile";
import { usePagesContent, useBaseEntityInfo, useBaseEntityIdLabel } from "../../../../Hooks/usePagesContent";
import { encryptTheParams } from "../../../../Helper/QueryParams/EncryptDecrypt";
import { getSOHScatterDetailsTableData, getSOHDetailsTableAction } from "../../../../Api/BatteryHealthApi";
import CODE from "../../../../Static/Constants/StatusCodes";
import {
  flattenRow,
  formatComputedDate,
  replaceInternalIdColumns,
  withDeviceIdField,
  buildBatteryListTitle,
  buildTableFiltersKey,
  narrowRequestFields,
} from "../utils/tableUtils";

const DEFAULT_PAGE_SIZE = 10;

// Fields the cell renderers read off the row by fixed name instead of through a column's
// dataIndex, so they must be requested even when their own column is hidden: pingStatus
// (the status dot on the id column), deviceID (id-link fallback), addedOn (time-ago cell).
// batteryID is absent on purpose — flattenRow derives it from xLabel.
const RENDERER_REQUIRED_FIELDS = ["pingStatus", "deviceID", "addedOn"];

/**
 * Generic battery list table for SOH/RUL detail views.
 *
 * Props:
 *   schemaPath        - string key under BatteryIntelligence (e.g. "SOHDetails" or "RULDetails")
 *                       Used to look up pagesContent.metaInfo.BatteryIntelligence[schemaPath].table.BatteryList
 *   customRenderersFn - function(onBatteryIDClick) => customRenderers object
 *   getCSVFileName    - function() => string (CSV filename)
 *   defaultSortColumn - string (default: "sohEst")
 *   globalFilters     - object
 *   additionalFilters - array
 *   pageSize          - rows per page (default: 10)
 */
const BatteryListTable = ({
  schemaPath,
  customRenderersFn,
  getCSVFileName,
  defaultSortColumn = "sohEst",
  globalFilters = {},
  additionalFilters = [],
  pageSize = DEFAULT_PAGE_SIZE,
}) => {
  const pagesContent = usePagesContent();
  const { baseEntityTypeLable } = useBaseEntityInfo();
  const deviceIdLabel = useBaseEntityIdLabel("Device ID");
  const tableTitle = buildBatteryListTitle(baseEntityTypeLable);

  const tabsToShow = pagesContent?.metaInfo?.digitalTwin?.tabsToShow || [];
  const healthTabValue = tabsToShow.includes('batteryHealth')
    ? 'batteryHealth'
    : tabsToShow.includes('health')
    ? 'health'
    : 'overview';

  const onBatteryIDClick = useCallback((record) => {
    const hash = encryptTheParams({ batteryID: record.batteryID, tabValue: healthTabValue });
    window.open(`/specificBattery?${hash}`, "_blank", "noreferrer");
  }, [healthTabValue]);

  const customRenderers = useMemo(
    () => customRenderersFn(onBatteryIDClick),
    [customRenderersFn, onBatteryIDClick]
  );

  // ─── orgMeta state ─────────────────────────────────────────────────────────
  const [orgMetaSelect,   setOrgMetaSelect]   = useState([]);
  const [orgMetaMetaInfo, setOrgMetaMetaInfo] = useState([]);
  const [schemaReady,     setSchemaReady]     = useState(false);

  // Stable reference — a fresh array literal here would change the fetch effect's
  // dependencies on every render and refetch for no reason.
  const defaultSort = useMemo(
    () => [{ column: defaultSortColumn, direction: "ASC" }],
    [defaultSortColumn]
  );

  // ─── Table state ───────────────────────────────────────────────────────────
  const [tableData,            setTableData]            = useState([]);
  const [isLoading,            setIsLoading]            = useState(false);
  const [total,                setTotal]                = useState(0);
  const [pageNumber,           setPageNumber]           = useState(1);
  const [sortBy,               setSortBy]               = useState(defaultSort);
  const [initialColumns,       setInitialColumns]       = useState([]);
  const [tableColumns,         setTableColumns]         = useState([]);
  const [selectedColumnKeys,   setSelectedColumnKeys]   = useState([]);
  const [defaultSelectedKeys,  setDefaultSelectedKeys]  = useState([]);
  const [columnOrder,          setColumnOrder]          = useState([]);
  const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
  const [isSelectAll,          setIsSelectAll]          = useState(false);

  const pinBoundaryColumns = useCallback((columns = []) => {
    if (columns.length === 0) return [];
    return columns.map((col, idx) => {
      if (idx === 0) return { ...col, fixed: "left" };
      return { ...col, fixed: undefined };
    });
  }, []);

  const initColumnsFromConfig = useCallback((config, order) => {
    const cols = getColumnsConfig({ tableColumns: config, tableColumnOrder: order, customRenderers });
    const pinned = pinBoundaryColumns(cols);
    setInitialColumns(pinned);
    const defaultSelected = pinBoundaryColumns(pinned.slice(0, MAX_COLUMN_SELECTION_COUNT));
    const defaultKeys = defaultSelected.map((c) => c.dataIndex);
    setTableColumns(defaultSelected);
    setSelectedColumnKeys(defaultKeys);
    setDefaultSelectedKeys(defaultKeys);
    setColumnOrder(defaultSelected.map((c) => c.headerLabel));
  }, [customRenderers, pinBoundaryColumns]);

  // ─── Read orgMeta schema from pagesContent ────────────────────────────────
  useEffect(() => {
    const tableSchema = pagesContent?.metaInfo?.BatteryIntelligence?.[schemaPath]?.table?.BatteryList;
    if (tableSchema) {
      setOrgMetaSelect(tableSchema.select   || []);
      setOrgMetaMetaInfo(tableSchema.metaInfo || []);
      // Never the internal batteryID, on screen or in the download / email built
      // from these columns: an org meta battery id column shows the device id.
      initColumnsFromConfig(
        replaceInternalIdColumns(tableSchema.columnsConfig, deviceIdLabel, tableSchema.columnOrdering),
        tableSchema.columnOrdering || []
      );
    }
    // Always mark ready so the table never stays permanently stuck when schema is absent.
    setSchemaReady(true);
  }, [pagesContent, schemaPath, initColumnsFromConfig, deviceIdLabel]);

  // ─── Reset pagination when filters change ─────────────────────────────────
  // Adjusted during render, not in an effect. In an effect the reset landed one commit
  // late: the fetch effect had already run with the new filters but the previous page and
  // sort, and the reset's state change then triggered a second, identical fetch. React
  // re-renders on a set-during-render before committing effects, so the fetch effect runs
  // once, already holding page 1 and the default sort.
  const filtersKey = useMemo(
    () => buildTableFiltersKey(globalFilters, additionalFilters, defaultSortColumn),
    [globalFilters, additionalFilters, defaultSortColumn]
  );
  const [appliedFiltersKey, setAppliedFiltersKey] = useState(filtersKey);
  if (appliedFiltersKey !== filtersKey) {
    setAppliedFiltersKey(filtersKey);
    setPageNumber(1);
    setSortBy(defaultSort);
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  }

  // ─── Only request the fields this view renders ─────────────────────────────
  // The org meta select/metaInfo lists describe every AVAILABLE column, so sending them
  // verbatim made the API compute all 21 columns (including four strings' worth of joined
  // SOH) to display four. Narrow to the selected columns, plus the renderer inputs and the
  // active sort column, which are needed whether or not their own column is visible.
  const requestExtraFields = useMemo(
    () => [...RENDERER_REQUIRED_FIELDS, sortBy[0]?.column].filter(Boolean),
    [sortBy]
  );
  const requestSelect = useMemo(
    () => narrowRequestFields(orgMetaSelect, selectedColumnKeys, requestExtraFields),
    [orgMetaSelect, selectedColumnKeys, requestExtraFields]
  );
  // The device id is always asked for: the id column and the export identify rows
  // by it, and narrowRequestFields keeps only what the org meta metaInfo lists.
  const requestMetaInfo = useMemo(
    () => withDeviceIdField(narrowRequestFields(orgMetaMetaInfo, selectedColumnKeys, requestExtraFields)),
    [orgMetaMetaInfo, selectedColumnKeys, requestExtraFields]
  );

  // ─── Fetch table data ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!schemaReady) return;
    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const res = await getSOHScatterDetailsTableData(
          globalFilters,
          requestSelect,
          requestMetaInfo,
          additionalFilters,
          pageNumber,
          pageSize,
          sortBy
        );
        if (cancelled) return;

        if (res?.responseStatus?.code === CODE.SUCCESS) {
          const items = res?.response?.data ?? [];
          const paginationInfo = res?.response?.paginationInfo;
          setTableData(items.map(flattenRow));
          setTotal(paginationInfo?.total ?? res?.response?.total ?? items.length);
        } else {
          setTableData([]);
          setTotal(0);
        }
      } catch (err) {
        console.error(`BatteryListTable (${schemaPath}): fetch error`, err);
        if (!cancelled) { setTableData([]); setTotal(0); }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [schemaReady, schemaPath, globalFilters, additionalFilters, pageNumber, pageSize, sortBy, requestSelect, requestMetaInfo]);

  // ─── Table interaction handlers ───────────────────────────────────────────
  const handlePageChange = useCallback((page) => {
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
    setPageNumber(page);
  }, []);

  const handleSortChange = useCallback((key, order) => {
    setSortBy([{ column: key, direction: order === "asc" ? "ASC" : "DESC" }]);
    setPageNumber(1);
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  }, []);

  const getCurrentSorting = useCallback(() => {
    if (sortBy.length > 0) return { sortBy: sortBy[0].column, orderBy: sortBy[0].direction.toLowerCase() };
    return {};
  }, [sortBy]);

  const onSelectChange = useCallback((record, selected) => {
    const updated = new Set(totalSelectedRowKeys);
    if (selected) updated.add(record.id);
    else {
      updated.delete(record.id);
      if (isSelectAll) setIsSelectAll(false);
    }
    setTotalSelectedRowKeys(updated);
  }, [totalSelectedRowKeys, isSelectAll]);

  const onSelectAllChange = useCallback((selected, selectedRows) => {
    setIsSelectAll(selected);
    setTotalSelectedRowKeys(
      selected ? new Set(selectedRows.map((r) => r?.id)) : new Set()
    );
  }, []);

  const onColumnOrderChange = useCallback((newTableColumns) => {
    const pinned = pinBoundaryColumns(newTableColumns);
    setTableColumns(pinned);
    setColumnOrder(pinned.map((c) => c.headerLabel));
  }, [pinBoundaryColumns]);

  const applySelectedColumns = useCallback((newSelectedKeys) => {
    const filtered = initialColumns.filter((c) => newSelectedKeys.includes(c.dataIndex));
    const pinned = pinBoundaryColumns(filtered);
    setTableColumns(pinned);
    setSelectedColumnKeys(newSelectedKeys);
    setColumnOrder(pinned.map((c) => c.headerLabel));
  }, [initialColumns, pinBoundaryColumns]);

  // Download/mail reuse the narrowed field lists: the exported columns are exactly
  // tableColumns, which is the selected set, so anything narrowed away was never exported.
  const buildActionBody = useCallback((actionType, emailParams = {}) => ({
    actionType,
    select: requestSelect,
    groupBy: ["batteryID"],
    metaInfo: requestMetaInfo,
    filters: additionalFilters,
    columns: tableColumns.map((c) => ({ dataIndex: c.dataIndex, headerLabel: c.headerLabel })),
    columnOrder: tableColumns.map((c) => c.dataIndex),
    selectAll: isSelectAll,
    resourceIDs: Array.from(totalSelectedRowKeys),
    sortBy: sortBy[0]?.column || defaultSortColumn,
    orderBy: sortBy[0]?.direction || "ASC",
    fileName: getCSVFileName(),
    ...emailParams,
  }), [requestSelect, requestMetaInfo, additionalFilters, tableColumns, isSelectAll, totalSelectedRowKeys, sortBy, defaultSortColumn, getCSVFileName]);

  const getCSVData = useCallback(async () => {
    const res = await getSOHDetailsTableAction(globalFilters, buildActionBody("download"));
    const rows = res?.response?.data ?? [];

    // Data-transform parity: "date" columns (e.g. sohComputedDate) render as
    // "DD MMM YYYY" in the table, so the downloaded CSV must match. CSV rows are
    // keyed by headerLabel — resolve each date column's headerLabel first.
    const dateHeaderLabels = tableColumns
      .filter((c) => c.valueRenderingType === "date")
      .map((c) => c.headerLabel || c.title)
      .filter(Boolean);

    if (dateHeaderLabels.length === 0) return rows;

    return rows.map((row) => {
      const next = { ...row };
      dateHeaderLabels.forEach((key) => {
        if (key in next) next[key] = formatComputedDate(next[key]);
      });
      return next;
    });
  }, [globalFilters, buildActionBody, tableColumns]);

  const onSendEmail = useCallback(async ({ sendTo, sendCc, subject, body }) => {
    await getSOHDetailsTableAction(globalFilters, buildActionBody("mail", {
      emailTo: sendTo,
      emailCc: sendCc,
      subject,
      body,
    }));
  }, [globalFilters, buildActionBody]);

  const getCsvColumnSettings = useCallback(() => ({
    columnOrder: tableColumns.map((c) => c.headerLabel),
  }), [tableColumns]);

  return (
    <Box sx={{ flex: 1, minWidth: 0, width: "100%", maxWidth: "100%", overflowX: "hidden", boxSizing: "border-box" }}>
      <GenericDraggableTableView
        title={tableTitle}
        showPrimaryButton={false}
        count={total}
        tableColumns={tableColumns}
        selectedColumns={selectedColumnKeys}
        tableData={tableData}
        availableEmailDomains={pagesContent?.metaInfo?.availableEmailDomains || []}
        initialColumns={initialColumns}
        applySelectedColumns={applySelectedColumns}
        defaultSelectedColumns={defaultSelectedKeys}
        isLoading={isLoading}
        disablePagination={false}
        pageNumber={pageNumber}
        rowsPerPage={pageSize}
        total={total}
        disableSearch={true}
        filters={{}}
        selectedFilters={{}}
        applyFilters={() => {}}
        disableFiltersSettings={true}
        columnOrder={columnOrder}
        onColumnOrderChange={onColumnOrderChange}
        applySort={handleSortChange}
        initialSorting={getCurrentSorting()}
        emptyMessage="No batteries found"
        containerStyles={{
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
          border: "1px solid #E6E6E6",
          overflow: "hidden",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
        getTotalRowSelection={() => Array.from(totalSelectedRowKeys)}
        getTotalRowSelectionCount={() =>
          isSelectAll ? total : totalSelectedRowKeys.size
        }
        rowSelection={{
          selectedRowKeys: Array.from(totalSelectedRowKeys),
          onSelect: onSelectChange,
          onSelectAll: onSelectAllChange,
        }}
        onClearSelection={() => { setTotalSelectedRowKeys(new Set()); setIsSelectAll(false); }}
        onPageChange={handlePageChange}
        getCSVFileName={getCSVFileName}
        getCSVData={getCSVData}
        onSendEmail={onSendEmail}
        getCsvColumnSettings={getCsvColumnSettings}
      />
    </Box>
  );
};

export default BatteryListTable;
